import { Button, Cell, Column, MenuTrigger, Pill, Row, Table, TableBody, TableHeader } from '@eleveo/components-react';
import { BinIcon, CheckmarkIcon, EyeIcon, PencilIcon, ThreeDotsIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { isSameDay, parseAbsolute, parseAbsoluteToLocal, toCalendarDate } from '@internationalized/date';
import React, { useMemo } from 'react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { useTranslation } from 'react-i18next';

import { LIST_OF_FORECASTS_CONTAINER_TAG, getForecastPropTag } from './constants';
import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { Forecast, Queue, StateEnum } from '../../graphql/components';
import { PATH_FORECASTING_STUDIO_EDIT, PATH_FORECASTING_STUDIO_VIEW } from '../../router/routes';
import { recordBy } from '../../utils/array';
import { isNonNullable } from '../../utils/guards';

const STATE_OPTIONS = {
  NON_PRODUCTION: {
    color: 'warning',
    icon: <PencilIcon />,
    nameKey: 'inDraft',
    variant: 'solid',
  },
  PRODUCTION: {
    color: 'success',
    icon: <CheckmarkIcon />,
    nameKey: 'inProduction',
    variant: 'outline',
  },
} as const;

type StateProps = {
  children: Forecast['state'];
};

const ProductionStatus = ({ children }: StateProps) => {
  const { t } = useTranslation();

  const { color, icon, nameKey, variant } = STATE_OPTIONS[children];

  return (
    <Pill color={color} variant={variant}>
      {icon} {t(`layout.states.${nameKey}`)}
    </Pill>
  );
};

type ForecastsTableProps = {
  forecasts: Forecast[];
  queues: Queue[];
  onDelete: (forecast: Forecast) => void;
};

export const ForecastsTable = ({ forecasts, queues, onDelete }: ForecastsTableProps) => {
  const { userHasPermissions } = useAuth();
  const { t } = useTranslation();

  const formatter = useDateFormatter({
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });

  const hasEditPermission = userHasPermissions([Permissions.EditForecast]);
  const hasDeletePermission = userHasPermissions([Permissions.DeleteForecast]);

  const columns = [
    {
      key: 'name',
      name: t('form.name'),
    },
    {
      key: 'status',
      name: t('dataImport.status.status'),
    },
    {
      key: 'queue',
      name: t('queue.queue'),
    },
    {
      align: 'center' as const,
      key: 'range',
      name: t('forecast.forecastRange'),
    },
    {
      align: 'center' as const,
      key: 'updated',
      name: t('form.updated'),
    },
    {
      key: 'actions',
      name: t('layout.action.actions'),
      isActions: true,
      hideHeader: true,
    },
  ];

  const queuesRecord = useMemo(() => recordBy(queues, 'id'), [queues]);

  const rows = forecasts.map((forecast) => {
    const {
      id,
      name,
      state,
      queueId,
      forecastRange: { startDateTime, endDateTime },
      timezone,
      modificationDate,
    } = forecast;

    const start = toCalendarDate(parseAbsolute(startDateTime, timezone));
    const end = toCalendarDate(parseAbsolute(endDateTime, timezone).subtract({ milliseconds: 1 }));

    return {
      id,
      name,
      status: <ProductionStatus>{state}</ProductionStatus>,
      queue: queuesRecord[queueId].name,
      range: isSameDay(start, end)
        ? formatter.format(start.toDate(timezone))
        : `${formatter.format(start.toDate(timezone))} - ${formatter.format(end.toDate(timezone))}`,
      updated: modificationDate ? formatter.format(parseAbsoluteToLocal(modificationDate).toDate()) : undefined,
      actions: (
        <MenuTrigger
          items={[
            {
              id: '1',
              icon: <EyeIcon />,
              href: `${PATH_FORECASTING_STUDIO_VIEW}/${id}`,
              testId: getForecastPropTag('view'),
              text: t('layout.action.view'),
            },
            hasEditPermission
              ? {
                  id: '2',
                  icon: <PencilIcon />,
                  href: `${PATH_FORECASTING_STUDIO_EDIT}/${id}`,
                  testId: getForecastPropTag('edit'),
                  text: t('layout.action.edit'),
                }
              : null,
            hasDeletePermission
              ? {
                  id: '3',
                  icon: <BinIcon />,
                  isDisabled: state === StateEnum.Production,
                  onClick: () => onDelete(forecast),
                  testId: getForecastPropTag('delete'),
                  text: t('layout.action.delete'),
                }
              : null,
          ].filter(isNonNullable)}
        >
          <Button
            aria-label={t('layout.action.actions')}
            isSmall
            testId={getForecastPropTag('actions')}
            variant="quiet"
          >
            <ThreeDotsIcon />
          </Button>
        </MenuTrigger>
      ),
    };
  });

  return (
    <Table aria-label={t('forecast.forecast', { count: 2 })} isStickyHeader testId={LIST_OF_FORECASTS_CONTAINER_TAG}>
      <TableHeader columns={columns}>
        {({ key, name, ...columnProps }) => (
          <Column key={key} {...columnProps}>
            {name}
          </Column>
        )}
      </TableHeader>
      <TableBody items={rows}>{(item) => <Row>{(columnKey) => <Cell>{item[columnKey]}</Cell>}</Row>}</TableBody>
    </Table>
  );
};
